import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PropertyCard = ({ property }) => {
  const { t } = useTranslation();

  const getBadgeLabel = (badge) => {
    const badges = {
      popular: t('common.popular'),
      new: t('common.new'),
      luxury: t('common.luxury'),
      seaView: t('common.seaView'),
      beachfront: t('common.beachfront'),
      center: t('common.center'),
    };
    return badges[badge] || badge;
  };

  return (
    <Link to={`/properties/${property.id}`}>
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="group relative bg-white dark:bg-surface-dark rounded-2xl overflow-hidden shadow-lg border border-gray-200 dark:border-white/10 hover:border-primary/50 flex flex-col h-full hover:shadow-[0_20px_40px_rgba(212,175,53,0.15)] transition-all duration-300"
      >
        {/* Image Container */}
        <div className="relative h-72 overflow-hidden">
          <motion.div 
            className="absolute inset-0 bg-cover bg-center" 
            style={{ backgroundImage: `url(${property.image})` }}
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Badge */}
          {property.badge && (
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className={`absolute top-4 left-4 backdrop-blur-md border border-white/20 text-xs font-bold px-3 py-1.5 rounded-lg uppercase tracking-wider shadow-lg ${property.badgeColor || 'bg-black/60 text-primary'}`}
            >
              {getBadgeLabel(property.badge)}
            </motion.div>
          )}

          {/* Price Tag */}
          <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
            <div className="text-white">
              <p className="text-2xl font-bold text-primary">{property.pricePerNight || property.price}<span className="text-sm text-white/70 font-normal"> {t('common.perNight')}</span></p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col flex-1">
          <div className="flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-wider mb-2">
            <span className="material-symbols-outlined text-sm">location_on</span>
            {property.location}
          </div>
          
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 group-hover:text-primary transition-colors line-clamp-2">
            {property.name}
          </h3>
          
          {/* Stats */}
          <div className="mt-auto pt-4 border-t border-gray-100 dark:border-white/10 grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 dark:bg-white/5 group-hover:bg-primary/10 transition-colors">
              <span className="material-symbols-outlined text-lg text-primary mb-1">bed</span>
              <span className="text-xs font-bold text-slate-900 dark:text-white">{property.beds} {t('common.beds')}</span>
            </div>
            <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 dark:bg-white/5 group-hover:bg-primary/10 transition-colors">
              <span className="material-symbols-outlined text-lg text-primary mb-1">bathtub</span>
              <span className="text-xs font-bold text-slate-900 dark:text-white">{property.baths} {t('common.baths')}</span>
            </div>
            <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 dark:bg-white/5 group-hover:bg-primary/10 transition-colors">
              <span className="material-symbols-outlined text-lg text-primary mb-1">group</span>
              <span className="text-xs font-bold text-slate-900 dark:text-white">{property.guests} {t('common.guests')}</span>
            </div>
          </div>

          {/* View Details - appears on hover */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileHover={{ opacity: 1, y: 0 }}
            className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <span className="flex items-center justify-center gap-2 text-primary font-bold text-sm">
              {t('common.viewDetails')}
              <span className="material-symbols-outlined text-lg group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </span>
          </motion.div>
        </div>
      </motion.div>
    </Link>
  );
};

export default PropertyCard;
